import { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Header from "../../layouts/header/Header";
import { useTranslation } from "react-i18next";
import { ProductContext } from "../../context/ProductContext";
import ProductSlider from "../../components/product/ProductSlider";
import Footer from "../footer/Footer";
import Subscription from "../footer/Subscription";
import ProductSliderHorizontal from "../../components/product/RecommendedSlider";
import ProductCard from "../../components/product/ProductCard";
import { Link, useNavigate } from "react-router-dom";
import { useNavigation } from "../../utils/navigation";

import ScrollableGrid from "../../components/section/ScrollableGrid";
import SwiperWrapper from "../../components/section/SwiperWrapper";
import AboutUsSection from "../../components/section/AboutUsSection";
import APIService from "../../services/APIService";
import Spinner from "../../components/animations/Spinner";
import ProductCardHorizontal from "../../components/product/ProductCardHorizontal";

const Container = styled.article`
  color: ${(props) => props.theme.textColor};
  padding-top: 72px;
`;
const Section = styled.section`
  //height: 100vh;
  @media (min-width: 765px) {
    margin-top: 80px;
  }
`;
const Grid = styled.div`
  display: flex;

  gap: 12px;
  cursor: grab;
  @media (min-width: 765px) {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
  }
  &:active {
    cursor: grabbing; /* Change cursor when actively dragging */
  }
`;
const PromoTitle = styled.h2`
  font-size: var(--font-size-xlarge);

  //margin: 40px 0 14px 0;
`;
const GridCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  margin: 10px 0;
  @media (min-width: 765px) {
    gap: 14px;
  }
`;
const CategoryItem = styled.div`
  background-color: var(--bg-100);
  padding: 8px 14px;
  font-size: var(--font-size-large);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--bg-300);
  text-transform: uppercase;
  box-sizing: border-box;
  border-radius: 8px;
  flex: 1;
  max-width: 200px;
  &:hover {
    background-color: var(--bg-200);
    border: 1px solid var(--text-200);
    cursor: pointer;
    transition: all 0.4s;
  }
`;
const CustomLink = styled(Link)`
  display: block;
  color: var(--text-color);
  font-size: var(--font-size-base);
  //width: 100%;
  margin: 14px 0;
  @media (min-width: 765px) {
    margin: 0;
  }
`;
const FlexDiv = styled.div`
  width: 100%;

  margin: 14px 0;
  @media (min-width: 765px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;
const Br = styled.div`
  height: 80px;
`;
const Disclaimer = styled.div`
  font-size: var(--font-size-large);
  @media (min-width: 765px) {
    font-size: var(--font-size-xxlarge);
  }
  border: 2px solid var(--text-100);
  color: var(--text-100);
  border-radius: 8px;
  text-align: center;
  padding: 14px 0;
  margin: 24px 0;
`;
const GridItem = styled.div`
  @media (max-width: 765px) {
    min-width: 200px;
    width: 200px;
  }
`;
const HomeMain = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { goToCategory, goToNewArrivals, goToBestSeller } = useNavigation();
  const [newArrivalsOpenIndex, setNewArrivalsOpenIndex] = useState(null);
  const [topBrandsOpenIndex, setTopBrandsOpenIndex] = useState(null);

  const {
    products,
    brands,
    loadProducts,
    loadBrands,
    loadFeaturedProducts,
    loadNewArrivalsProducts,
    featuredProducts,
    newArrivals,
  } = useContext(ProductContext);

  const [brandData, setBrandData] = useState({});

  useEffect(() => {
    //loadFeaturedProducts();
    loadNewArrivalsProducts();
    loadBrands();
  }, []);

  const observerRefs = useRef({});
  const observer = useRef(null);

  const PRIORITY_BRANDS = ["velo", "skruf", "thunder", "cuba", "white-fox"];
  const selectedBrands = PRIORITY_BRANDS.map((slug) =>
    brands.find((b) => b.slug === slug)
  ).filter(Boolean);

  const topBrands = selectedBrands.slice(0, 2); // VELO, SKRUF
  const bottomBrands = selectedBrands.slice(2);

  useEffect(() => {
    if (!brands.length) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          const slug = entry.target.getAttribute("data-brand");
          if (entry.isIntersecting && !brandData[slug]) {
            const response = await APIService.GetProductsByBrand(slug);
            setBrandData((prev) => ({
              ...prev,
              [slug]: response.results || [],
            }));
            observer.current.unobserve(entry.target); // zaustavi posle učitavanja
          }
        });
      },
      {
        threshold: 0.2, // vidi kada je bar 20% vidljivo
      }
    );

    brands.forEach((brand) => {
      const ref = observerRefs.current[brand.slug];
      if (ref) {
        observer.current.observe(ref);
      }
    });

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [brands]);

  const [openIndices, setOpenIndices] = useState([]);

  // useEffect(() => {
  //   if (brands.length > 0) {
  //     setOpenIndices(Array(brands.length).fill(null));
  //   }
  // }, [brands]);
  useEffect(() => {
    setOpenIndices(Array(selectedBrands.length).fill(null));
  }, [selectedBrands.length]);

  const handleSetOpenIndex = (sectionIndex, index) => {
    const newIndices = [...openIndices];
    newIndices[sectionIndex] =
      newIndices[sectionIndex] === index ? null : index;
    setOpenIndices(newIndices);
  };

  const handleSetNewArrivalsOpenIndex = (index) => {
    setNewArrivalsOpenIndex((prev) => (prev === index ? null : index));
  };

  const handleSetTopBrandsOpenIndex = (index) => {
    setTopBrandsOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <>
      <Header />
      <Container>
        <Section>
          {/*
          <FlexDiv>
            <PromoTitle>{t("HEADER.LINKS.TOP_BRANDS")}</PromoTitle>
            <CustomLink to={goToBestSeller()}>
              {t("BUTTONS.VIEW_ALL")}
            </CustomLink>
          </FlexDiv>
           <ProductSliderHorizontal products={featuredProducts} /> */}
          <Br />
          <FlexDiv>
            <PromoTitle>{t("HOME.NEW_ARRIVALS")}</PromoTitle>
            <CustomLink to={goToNewArrivals()}>
              {t("BUTTONS.VIEW_ALL")}
            </CustomLink>
          </FlexDiv>
          <ScrollableGrid>
            {newArrivals ? (
              newArrivals.slice(0, 5).map((product, i) => (
                <GridItem key={i}>
                  <ProductCard
                    key={i}
                    product={product}
                    isOpen={newArrivalsOpenIndex === i}
                    setIsOpen={() => handleSetNewArrivalsOpenIndex(i)}
                  />
                </GridItem>
              ))
            ) : (
              <Spinner />
            )}
          </ScrollableGrid>

          <Br />

          {/* PRVIH 5 BRENDOVA */}
          {topBrands.map((brand, brandIndex) => (
            <div
              key={brand.slug}
              ref={(el) => (observerRefs.current[brand.slug] = el)}
              data-brand={brand.slug}
            >
              <FlexDiv>
                <PromoTitle style={{ textTransform: "capitalize" }}>
                  {brand.name}
                </PromoTitle>
                <CustomLink to={goToCategory(brand.slug)}>
                  {t("BUTTONS.VIEW_ALL")}
                </CustomLink>
              </FlexDiv>

              <ScrollableGrid>
                {brandData[brand.slug] ? (
                  brandData[brand.slug].slice(0, 5).map((product, i) => (
                    <GridItem key={i}>
                      <ProductCard
                        key={i}
                        product={product}
                        isOpen={openIndices[brandIndex] === i}
                        setIsOpen={() => handleSetOpenIndex(brandIndex, i)}
                      />
                    </GridItem>
                  ))
                ) : (
                  <Spinner />
                )}
              </ScrollableGrid>
            </div>
          ))}

          <Br />
          <FlexDiv>
            <PromoTitle>{t("HOME.BRANDS")}</PromoTitle>
            <CustomLink to={`/${i18n.language}/unsere-marken`}>
              {t("BUTTONS.VIEW_ALL")}
            </CustomLink>
          </FlexDiv>

          <GridCategories>
            {brands.slice(0, 11).map((brand, index) => (
              <CategoryItem
                key={index}
                onClick={() =>
                  navigate(`${i18n.language}/snus-verkauf/${brand.slug}`)
                }
              >
                {brand.name}
              </CategoryItem>
            ))}
          </GridCategories>

          <Disclaimer>{t("HOME.DISCLAIMER")}</Disclaimer>

          {/* OSTALI BRENDOVI */}
          {bottomBrands.length > 0 && (
            <>
              <h3>{t("OTHER_BRANDS")}</h3>
              {bottomBrands.map((brand, brandIndex) => (
                <div
                  key={brand.slug}
                  ref={(el) => (observerRefs.current[brand.slug] = el)}
                  data-brand={brand.slug}
                >
                  <FlexDiv>
                    <PromoTitle style={{ textTransform: "capitalize" }}>
                      {brand.name}
                    </PromoTitle>
                    <CustomLink to={goToCategory(brand.slug)}>
                      {t("BUTTONS.VIEW_ALL")}
                    </CustomLink>
                  </FlexDiv>

                  <ScrollableGrid>
                    {brandData[brand.slug] ? (
                      brandData[brand.slug].slice(0, 5).map((product, i) => (
                        <GridItem key={i}>
                          <ProductCard
                            key={i}
                            product={product}
                            isOpen={openIndices[brandIndex + 5] === i}
                            setIsOpen={() =>
                              handleSetOpenIndex(brandIndex + 5, i)
                            }
                          />
                        </GridItem>
                      ))
                    ) : (
                      <Spinner />
                    )}
                  </ScrollableGrid>
                </div>
              ))}
            </>
          )}

          <SwiperWrapper items={brands.slice(0, 3)} />
          <AboutUsSection />
        </Section>
      </Container>

      <Subscription />
      <Footer />
    </>
  );
};

export default HomeMain;
